import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import InlineField from './InlineField';
import AgendaHoursCell from './AgendaHoursCell';
import StaffEditor from './StaffEditor';
import { exportPersonnelPdf } from './accessListPdf';
import { formatTime, parseTime } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { canRead, canWrite } from '../../utils/roles';
import { clientDisplayName } from '../../utils/clientName';
import { checklistBadge } from './agendaUtils';
import haptic from '../../utils/haptic';

// Cotización del evento (en el lugar del nº de Odoo de siempre):
// - creado desde una cotización de la app → folio clickeable (abre la cotización)
// - importado de Odoo (código "S...") → nº de Odoo, como siempre
// - serie LNG propia sin cotización, o sin código alguno → badge rojo clickeable;
//   con serie LNG muestra esa serie, sin ella "SIN COT." — clic crea la cotización
//   (hereda el número si hay serie) y la asocia al evento.
function QuoteBadge({ event }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canQuote = canWrite(user, 'ventas.cotizaciones');
  const [creating, setCreating] = useState(false);

  if (event.quote_id) {
    return (
      <span
        className="ev-odoo-code ev-quote-link"
        title="Abrir la cotización"
        onClick={e => { e.stopPropagation(); navigate(`/ventas/cotizaciones/${event.quote_id}`); }}
      >
        {event.quote_folio || `COT ${event.quote_id}`}
      </span>
    );
  }

  const isLngSeries = /^LNG\d+$/i.test(event.odoo_code || '');
  if (event.odoo_code && !isLngSeries) return <span className="ev-odoo-code">{event.odoo_code}</span>;

  const lngSeries = isLngSeries ? event.odoo_code.toUpperCase() : null;

  const create = async (e) => {
    e.stopPropagation();
    if (!canQuote || creating) return;
    setCreating(true);
    try {
      // Mismos ítems base que el alta de QuotesPage; título/fecha/lugar los
      // prellena el backend desde el evento.
      const { data } = await api.post('/quotes', {
        event_id: event.id,
        status: 'posible',
        items: [
          { section: 'General', description: 'General', quantity: 0, unit_price: 0, discount: 0, is_header: true, note: null, order: 0 },
          { section: 'General', description: '', quantity: 1, unit_price: 0, discount: 0, is_header: false, note: null, order: 1 },
        ],
      });
      navigate(`/ventas/cotizaciones/${data.id}`);
    } catch (err) {
      console.error(err);
      setCreating(false);
    }
  };

  const title = lngSeries
    ? `Proyecto ${lngSeries} sin cotización — clic para crear la cotización ${lngSeries}`
    : (canQuote ? 'Sin cotización — clic para crear una asociada a este evento' : 'Sin cotización asociada');

  return (
    <span
      className={`ev-odoo-code ev-quote-missing${canQuote ? ' clickable' : ''}`}
      title={title}
      onClick={canQuote ? create : undefined}
    >
      {creating ? 'Creando…' : (lngSeries || 'SIN COT.')}
    </span>
  );
}

// Normaliza horas con formatos inconsistentes ("8", "19.30", "08:00") a "HH:MM".
function normTime(t) {
  if (!t) return null;
  const s = String(t).trim();
  if (/^\d+\.\d+$/.test(s)) return s.replace('.', ':').padStart(5, '0');
  if (/^\d{1,2}$/.test(s)) return `${s.padStart(2, '0')}:00`;
  if (/^\d{1,2}:\d{2}/.test(s)) return s.slice(0, 5).padStart(5, '0');
  return null;
}

// El evento está sucediendo AHORA: es hoy y la hora actual cae en su ventana.
function isEventLive(event) {
  if (event.status !== 'confirmado' || !event.event_date) return false;
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  if (event.event_date.slice(0, 10) !== today) return false;
  const start = normTime(event.start_time);
  if (!start) return false;
  const nowHM = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const end = normTime(event.end_time);
  if (!end || end < start) return nowHM >= start; // sin fin u overnight: en vivo hasta medianoche
  return nowHM >= start && nowHM <= end;
}

function StatusPicker({ status, canAdmin, eventId, onLoad, onUpdateEvent, live, warn, tabGroup }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const pillRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (pillRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Foco: al abrir, entra al menú (primer ítem); al cerrar con Escape, vuelve al pill.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => menuRef.current?.querySelector('button')?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // El pill se integra al esquema de tabulación propio de la fila (tabGroup):
  // ocupa el índice -1, "antes" del campo 0 — así Shift+Tab desde el primer
  // campo editable de la fila lo alcanza, sin tocar la numeración existente.
  useEffect(() => {
    if (tabGroup) tabGroup.current[-1] = canAdmin ? openMenu : undefined;
  });

  async function pick(s) {
    setOpen(false);
    pillRef.current?.focus();
    try {
      await api.patch(`/events/${eventId}/status`, { status: s });
      onUpdateEvent?.({ status: s });
    } catch (e) {
      console.error(e);
      onLoad();
    }
  }

  function openMenu() {
    if (pillRef.current) {
      const rect = pillRef.current.getBoundingClientRect();
      setDropPos({ top: rect.top, left: rect.right + 6 });
    }
    setOpen(true);
  }

  function toggle(e) {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    openMenu();
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(e);
    }
  }

  function onMenuKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      pillRef.current?.focus();
    }
  }

  const ariaLabel = `Estado: ${status}${live ? ' · en vivo' : ''}${warn ? ' · sin confirmar, el evento se acerca' : ''}${canAdmin ? ' — cambiar' : ''}`;

  // El estado vive en la barra izquierda de la fila (color por estado);
  // "live" agrega el destello de evento en curso. Clic/Enter/Espacio (admin) abre el menú.
  const bar = (
    <span
      ref={pillRef}
      className={`ev-status-bar ${status}${live ? ' live' : ''}${warn ? ' warn' : ''}${canAdmin ? ' clickable' : ''}`}
      title={`${status}${live ? ' · EN VIVO' : ''}${warn ? ' · SIN CONFIRMAR — el evento se acerca' : ''}${canAdmin ? ' — clic para cambiar' : ''}`}
      aria-label={ariaLabel}
      role={canAdmin ? 'button' : undefined}
      tabIndex={canAdmin ? 0 : undefined}
      aria-haspopup={canAdmin ? 'menu' : undefined}
      aria-expanded={canAdmin ? open : undefined}
      onClick={canAdmin ? toggle : undefined}
      onKeyDown={canAdmin ? onKeyDown : undefined}
    />
  );

  if (!canAdmin) return bar;

  return (
    <>
      {bar}
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}>
          <div ref={menuRef} className="status-dropdown" role="menu" onKeyDown={onMenuKeyDown}>
            <div className="status-dropdown-label">Cambiar estado</div>
            {['confirmado', 'posible', 'cancelado'].map(s => (
              <button key={s} type="button" role="menuitem" className={`status-dropdown-item ${s}`} onClick={() => pick(s)}>
                {s}
                {status === s && <span className="status-dropdown-check">✓</span>}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// El evento posible que se acerca sin confirmar: la franja ámbar vibra desde
// 3 días antes hasta la hora de inicio, como señal de que hay que definirlo.
function isPosibleUrgent(event) {
  if (event.status !== 'posible' || !event.event_date) return false;
  const d = event.event_date.slice(0, 10);
  const [y, m, dd] = d.split('-').map(Number);
  if (!y || !m || !dd) return false;
  const start = normTime(event.start_time) || '00:00';
  const evStart = new Date(y, m - 1, dd, Number(start.slice(0, 2)), Number(start.slice(3, 5)));
  const diffMs = evStart - new Date();
  return diffMs > 0 && diffMs <= 3 * 24 * 3600 * 1000;
}

// Lugar del evento: siempre sale de la lista maestra (Ventas → Lugares), con
// sus sub-lugares como "Lugar - Espacio" — no es texto libre. Buscable.
export function LugarPicker({ value, lugares, canEdit, onSave }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (anchorRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll(e) {
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  function toggle(e) {
    e.stopPropagation();
    if (!canEdit) return;
    if (!open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left });
    }
    setQuery('');
    setOpen(o => !o);
  }

  function pick(name) {
    setOpen(false);
    if (name !== value) onSave(name);
  }

  const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const options = [];
  for (const l of (lugares || [])) {
    options.push(l.name);
    for (const esp of (l.espacios || [])) options.push(`${l.name} - ${esp.name}`);
  }
  const q = norm(query);
  const filtered = q ? options.filter(o => norm(o).includes(q)) : options;

  return (
    <>
      <span
        ref={anchorRef}
        className={`ev-referent${canEdit ? ' ev-referent-clickable' : ''}`}
        onClick={toggle}
        title={canEdit ? 'Lugar — clic para elegir de la lista' : 'Lugar'}
      >
        {value || <span className="ev-referent-empty">Lugar</span>}
      </span>
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}>
          <div ref={menuRef} className="status-dropdown ev-lugar-menu">
            <input
              className="ev-lugar-search"
              autoFocus
              placeholder="Buscar lugar…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && filtered.length > 0) { e.preventDefault(); pick(filtered[0]); }
              }}
            />
            <div className="ev-lugar-options">
              {value && (
                <button type="button" className="status-dropdown-item" onClick={() => pick('')}>
                  Quitar lugar
                </button>
              )}
              {/* Valor viejo fuera de la lista: se conserva visible */}
              {value && !options.includes(value) && (
                <button type="button" className="status-dropdown-item" onClick={() => pick(value)}>
                  {value} <span className="status-dropdown-check">✓</span>
                </button>
              )}
              {filtered.slice(0, 60).map(name => (
                <button key={name} type="button" className="status-dropdown-item" onClick={() => pick(name)}>
                  {name}
                  {value === name && <span className="status-dropdown-check">✓</span>}
                </button>
              ))}
              {filtered.length === 0 && <div className="ev-lugar-none">Sin resultados</div>}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// Cliente del evento: siempre de la lista maestra de clientes — nunca texto
// libre. Clic abre un menú buscable. Un valor viejo fuera de la lista (venido
// de la planilla) se conserva visible.
export function ClientPicker({ value, clients, canEdit, onSave }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (anchorRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll(e) {
      if (menuRef.current?.contains(e.target)) return; // scroll interno del menú
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  function toggle(e) {
    e.stopPropagation();
    if (!canEdit) return;
    if (!open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left });
    }
    setQuery('');
    setOpen(o => !o);
  }

  function pick(name) {
    setOpen(false);
    if (name !== value) onSave(name);
  }

  const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  // display → razones sociales asociadas: la búsqueda matchea ambas sin
  // recorrer la lista entera por cada nombre.
  const razonByDisplay = {};
  for (const c of (clients || [])) {
    const d = clientDisplayName(c);
    if (d) (razonByDisplay[d] = razonByDisplay[d] || []).push(norm(c.name || ''));
  }
  const names = Object.keys(razonByDisplay);
  const q = norm(query);
  const filtered = q
    ? names.filter(n => norm(n).includes(q) || razonByDisplay[n].some(r => r.includes(q)))
    : names;

  return (
    <>
      <span
        ref={anchorRef}
        className={`ev-referent${canEdit ? ' ev-referent-clickable' : ''}`}
        onClick={toggle}
        title={canEdit ? 'Cliente — clic para elegir de la lista' : 'Cliente'}
      >
        {value || <span className="ev-referent-empty">Cliente</span>}
      </span>
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}>
          <div ref={menuRef} className="status-dropdown ev-lugar-menu">
            <input
              className="ev-lugar-search"
              autoFocus
              placeholder="Buscar cliente…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && filtered.length > 0) { e.preventDefault(); pick(filtered[0]); }
              }}
            />
            <div className="ev-lugar-options">
              {value && (
                <button type="button" className="status-dropdown-item" onClick={() => pick('')}>
                  Quitar cliente
                </button>
              )}
              {value && !names.includes(value) && (
                <button type="button" className="status-dropdown-item" onClick={() => pick(value)}>
                  {value} <span className="status-dropdown-check">✓</span>
                </button>
              )}
              {filtered.slice(0, 60).map(name => (
                <button key={name} type="button" className="status-dropdown-item" onClick={() => pick(name)}>
                  {name}
                  {value === name && <span className="status-dropdown-check">✓</span>}
                </button>
              ))}
              {filtered.length === 0 && <div className="ev-lugar-none">Sin resultados</div>}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// Referente / vendedor del evento: sale de Personal, marcado como referente.
// Clic abre un menú buscable. El menú scrollea (el
// scroll interno no cierra el dropdown). No es texto libre.
export function ReferentPicker({ value, salesRefs, canEdit, onSave }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, maxH: 260 });
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (anchorRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll(e) {
      if (menuRef.current?.contains(e.target)) return; // no cerrar al scrollear DENTRO del menú
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  function toggle(e) {
    e.stopPropagation();
    if (!canEdit) return;
    if (!open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      // El alto del menú se acota al espacio hasta el borde inferior de la
      // ventana para que siempre pueda scrollear sin salirse de pantalla.
      const maxH = Math.max(140, Math.min(300, window.innerHeight - rect.bottom - 16));
      setDropPos({ top: rect.bottom + 4, left: rect.left, maxH });
    }
    setQuery('');
    setOpen(o => !o);
  }

  function pick(name) {
    setOpen(false);
    if (name !== value) onSave(name);
  }

  const norm = s => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const names = (salesRefs || []).map(u => u.name || u.email).filter(Boolean);
  const q = norm(query);
  const filtered = q ? names.filter(n => norm(n).includes(q)) : names;

  return (
    <>
      <span
        ref={anchorRef}
        className={`ev-referent${canEdit ? ' ev-referent-clickable' : ''}`}
        onClick={toggle}
        title={canEdit ? 'Referente — clic para elegir un vendedor' : 'Referente'}
      >
        {value || <span className="ev-referent-empty">Referente</span>}
      </span>
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}>
          <div ref={menuRef} className="status-dropdown ev-lugar-menu" style={{ maxHeight: dropPos.maxH, display: 'flex', flexDirection: 'column' }}>
            {names.length > 8 && (
              <input
                className="ev-lugar-search"
                autoFocus
                placeholder="Buscar vendedor…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') setOpen(false);
                  if (e.key === 'Enter' && filtered.length > 0) { e.preventDefault(); pick(filtered[0]); }
                }}
              />
            )}
            <div className="ev-lugar-options" style={{ overflowY: 'auto' }}>
              <button type="button" className="status-dropdown-item" onClick={() => pick('')}>
                Sin referente
                {!value && <span className="status-dropdown-check">✓</span>}
              </button>
              {value && !names.includes(value) && (
                <button type="button" className="status-dropdown-item" onClick={() => pick(value)}>
                  {value} <span className="status-dropdown-check">✓</span>
                </button>
              )}
              {filtered.map(name => (
                <button key={name} type="button" className="status-dropdown-item" onClick={() => pick(name)}>
                  {name}
                  {value === name && <span className="status-dropdown-check">✓</span>}
                </button>
              ))}
              {names.length === 0 && (
                <div className="ev-lugar-none">No hay vendedores. Marcalos en Personal.</div>
              )}
              {names.length > 0 && filtered.length === 0 && <div className="ev-lugar-none">Sin resultados</div>}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

const KebabMenu = React.forwardRef(function KebabMenu({ items, mobileFirstItem }, ref) {
  const [open, setOpen] = useState(false);
  const [viaLongPress, setViaLongPress] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  // Permite abrir el menú desde afuera (long-press de la fila en mobile) en una
  // posición dada, sin depender del botón ⋯ (que en mobile está oculto).
  // viaLongPress marca que se abrió en mobile → se antepone "Ver equipos".
  useImperativeHandle(ref, () => ({
    openAt(x, y) {
      setMenuPos({ top: Math.min(y, window.innerHeight - 220), right: Math.max(8, window.innerWidth - x) });
      setViaLongPress(true);
      setOpen(true);
    },
  }));

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // "Ver equipos" se antepone solo cuando se abrió por long-press (mobile).
  const displayItems = (mobileFirstItem && viaLongPress) ? [mobileFirstItem, ...items] : items;
  if (!displayItems.length) return null;

  function toggle(e) {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setViaLongPress(false);
    setOpen(o => !o);
  }

  return (
    <div className="kebab-wrap">
      <button ref={btnRef} className="kebab-btn" type="button" onClick={toggle} title="Más opciones">⋯</button>
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}>
          <div ref={menuRef} className="kebab-menu">
            {displayItems.map((item, i) => (
              <button
                key={i}
                type="button"
                className={`kebab-item${item.danger ? ' danger' : ''}`}
                onClick={() => { setOpen(false); item.onClick(); }}
                disabled={item.disabled}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});



export default function EventRow({
  event, index, canOperate: canOperateProp, canAdmin, onLoad, onEdit, onUpdateEvent, onRemoveEvent,
  canViewEventEquipment,
  isDragging, onDragStart, onDragEnd, canDrag: canDragProp, onScrollToArmado, onOpenChecklist,
  onCreateAuxOperation, onRequestMoveDate,
  personnel, assignmentModel, assignmentModelError, retryAssignmentModel, lugares, clients, salesRefs, kbFocused = false, conflictKeys,
}) {
  const { user } = useAuth();
  const canExportPersonnelPdf = canRead(user, 'agenda.personal-pdf');
  // Cancelado = cerrado: no se puede operar nada de la fila. El estado sigue
  // siendo cambiable (barra izquierda, admin) para poder reabrirlo.
  const locked = event.status === 'cancelado';
  const canOperate = canOperateProp && !locked;
  const canDrag = canDragProp && !locked;
  const navigate   = useNavigate();
  const tabGroup   = useRef([]);
  const [navigating, setNavigating] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const kebabRef = useRef(null);
  const menuTimer = useRef(null);
  const menuPressPos = useRef(null);
  const menuFired = useRef(false);
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  // Swipe (mobile): arrastrar la card a la izquierda revela y abre el checklist
  // (estilo iOS). Un swipe horizontal cancela el tap-expandir y el long-press.
  const rowRef = useRef(null);
  const swipe = useRef(null);        // { startX, startY, axis, dx }
  const swipeFired = useRef(false);
  const SWIPE_MAX = 112;             // arrastre máximo
  const SWIPE_OPEN = 72;             // umbral para abrir

  function setSwipeX(px, animate) {
    const el = rowRef.current;
    if (!el) return;
    el.classList.toggle('ev-swiping', !animate);   // sin transición mientras se arrastra
    el.classList.toggle('ev-swipe-armed', px <= -SWIPE_OPEN);
    el.style.setProperty('--ev-swipe-x', `${px}px`);
  }

  // En mobile la fila es una tarjeta compacta; tap corto la expande. No togglear
  // si el tap cae sobre un control interactivo, ni si el long-press abrió el menú,
  // ni si acabó de haber un swipe horizontal.
  function toggleMob(e) {
    if (!isMobile()) return;
    if (swipeFired.current) { swipeFired.current = false; return; }
    if (menuFired.current) { menuFired.current = false; return; }
    if (e.target.closest('button, a, input, textarea, select, .ev-status-bar, .ev-referent, .se-line, .se-sheet, .se-role-pop, .kebab-wrap, .agenda-portal')) return;
    setMobOpen(o => !o);
  }

  // Long-press en una zona NO editable → abre el menú de acciones (⋯) en mobile.
  function rowPointerDown(e) {
    if (!isMobile()) return;
    menuFired.current = false;
    swipeFired.current = false;
    // Swipe: permitido en casi toda la card, salvo controles reales
    if (!e.target.closest('input, textarea, select, button, a, .ev-status-bar, .kebab-wrap, .agenda-portal, .se-sheet, .se-role-pop')) {
      swipe.current = { startX: e.clientX, startY: e.clientY, axis: null, dx: 0 };
    }
    if (e.target.closest('.inline-trigger.editable, button, a, input, textarea, select, .ev-status-bar, .ev-referent, .se-line, .se-sheet, .se-role-pop, .agenda-portal')) return;
    const x = e.clientX, y = e.clientY;
    menuPressPos.current = { x, y };
    clearTimeout(menuTimer.current);
    menuTimer.current = setTimeout(() => {
      menuFired.current = true;
      haptic.tick();
      kebabRef.current?.openAt(x, y);
    }, 500);
  }
  function rowPointerMove(e) {
    if (menuPressPos.current && (Math.abs(e.clientX - menuPressPos.current.x) > 10 || Math.abs(e.clientY - menuPressPos.current.y) > 10)) clearTimeout(menuTimer.current);
    const s = swipe.current;
    if (!s) return;
    const dx = e.clientX - s.startX, dy = e.clientY - s.startY;
    if (s.axis === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      s.axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'h' : 'v';
      if (s.axis === 'v') { swipe.current = null; return; }   // scroll vertical
    }
    if (s.axis !== 'h') return;
    swipeFired.current = true;
    clearTimeout(menuTimer.current);
    const clamped = Math.max(-SWIPE_MAX, Math.min(0, dx));
    s.dx = clamped;
    setSwipeX(clamped, false);
  }
  function rowPointerEnd() {
    clearTimeout(menuTimer.current);
    menuPressPos.current = null;
    const s = swipe.current;
    swipe.current = null;
    if (!s || s.axis !== 'h') return;
    const commit = s.dx <= -SWIPE_OPEN;
    setSwipeX(0, true);          // vuelve animado
    if (commit) {
      haptic.tick();
      handleViewTeams();
    }
  }

  useEffect(() => {
    if (!kbFocused) return;
    function onEnter(e) {
      if (e.key === 'Enter') { e.preventDefault(); tabGroup.current[0]?.(); }
    }
    document.addEventListener('keydown', onEnter);
    return () => document.removeEventListener('keydown', onEnter);
  }, [kbFocused]);
  const [archiveOpen, setArchiveOpen]   = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState('');

  // Issue 1: update locally, no reload on success
  async function saveField(field, val) {
    try {
      await api.put(`/events/${event.id}`, { [field]: val || null });
      onUpdateEvent?.({ [field]: val || null });
    } catch {
      onLoad();
    }
  }

  function handleViewTeams() {
    if (!canViewEventEquipment || navigating) return;
    setNavigating(true);
    navigate(`/agenda/eventos/${event.id}/equipos`);
  }

  async function handleArchive() {
    try {
      await api.post(`/events/${event.id}/archive`, { reason: archiveReason });
      setArchiveOpen(false);
      setArchiveReason('');
      onRemoveEvent?.(event.id);
    } catch (e) { console.error(e); }
  }

  async function handleUnarchive() {
    try {
      await api.post(`/events/${event.id}/unarchive`);
      onRemoveEvent?.(event.id);
    } catch (e) { console.error(e); }
  }

  async function handleDuplicate() {
    if (!duplicateDate) return;
    try {
      await api.post(`/events/${event.id}/duplicate`, { event_date: duplicateDate });
      setDuplicateOpen(false);
      setDuplicateDate('');
      onLoad();
    } catch (e) { console.error(e); }
  }

  // Issue 8: "Ver checklist" → "Ver equipos" como botón destacado; menú solo Editar/Duplicar/Archivar
  const menuItems = [];
  if (canExportPersonnelPdf) {
    menuItems.push({ label: 'PDF Personal', onClick: () => exportPersonnelPdf({ entityType: 'event', entityId: event.id }) });
  }
  if (canAdmin)   menuItems.push({ label: 'Editar', onClick: onEdit });
  if (canAdmin)   menuItems.push({ label: 'Duplicar', onClick: () => { setDuplicateDate(event.event_date?.slice(0, 10) || ''); setDuplicateOpen(true); } });
  if (canAdmin && !event.archived) menuItems.push({ label: 'Archivar', onClick: () => { setArchiveReason(''); setArchiveOpen(true); }, danger: true });
  if (canAdmin &&  event.archived) menuItems.push({ label: 'Desarchivar', onClick: handleUnarchive });

  const zebraClass     = index % 2 === 0 ? 'ev-odd' : 'ev-even';
  const cancelledClass = event.status === 'cancelado' ? ' ev-cancelled' : '';
  const draggingClass  = isDragging ? ' is-dragging' : '';
  const archivedClass  = event.archived ? ' ev-archived' : '';
  const focusClass     = kbFocused ? ' row-kb-focus' : '';

  const badge        = checklistBadge(event);
  // El badge de checklist siempre está, así que la fila inferior también.

  const timeValue = f => formatTime(event[f]);

  return (
    <div
      ref={rowRef}
      className={`ev-row ${zebraClass}${cancelledClass}${draggingClass}${archivedClass}${focusClass}${mobOpen ? ' ag-mob-open' : ''}`}
      data-event-id={event.id}
      onClick={toggleMob}
      onPointerDown={rowPointerDown}
      onPointerMove={rowPointerMove}
      onPointerUp={rowPointerEnd}
      onPointerLeave={rowPointerEnd}
    >
      {/* Reveal de swipe (solo mobile): aparece por la derecha al arrastrar la card */}
      <div className="ev-swipe-reveal" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
        <span>Equipos</span>
      </div>
      {/* Col 1 — Orden (la barra izquierda es el estado; clic para cambiarlo) */}
      <div className="ev-cell ev-cell-order">
        <StatusPicker
          status={event.status}
          canAdmin={canAdmin}
          eventId={event.id}
          onLoad={onLoad}
          onUpdateEvent={onUpdateEvent}
          live={isEventLive(event)}
          warn={isPosibleUrgent(event)}
          tabGroup={tabGroup}
        />
        <span
          className="ev-drag-handle"
          title="Reordenar · doble click para cambiar fecha"
          draggable={canDrag}
          onDoubleClick={canAdmin ? e => {
            e.preventDefault();
            e.stopPropagation();
            onRequestMoveDate?.(event);
          } : undefined}
          onDragStart={canDrag ? e => {
            const rowEl = e.currentTarget.closest('[data-event-id]');
            const cells = rowEl ? Array.from(rowEl.children) : [];
            const firstCell = cells[0];
            const isLight = document.documentElement.dataset.theme === 'light';
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('application/x-depot-event', '');
            e.dataTransfer.setData('text/plain', JSON.stringify({ id: event.id, sourceDate: event.event_date?.slice(0, 10) }));
            if (firstCell) {
              // Expand display:contents children (se-cell → se-line×3) to get real grid items
              const visual = [];
              for (const c of cells) {
                if (window.getComputedStyle(c).display === 'contents') {
                  for (const gc of c.children) visual.push(gc);
                } else {
                  visual.push(c);
                }
              }
              const rects = visual.map(c => c.getBoundingClientRect());
              const colWidths = rects.map(r => `${Math.round(r.width)}px`);
              const totalWidth = rects.reduce((s, r) => s + r.width, 0);
              const scopeWrap = document.createElement('div');
              if (isLight) scopeWrap.dataset.theme = 'light';
              scopeWrap.style.cssText = 'position:fixed;top:-9999px;left:0;pointer-events:none;';
              const scopePage = document.createElement('div');
              scopePage.className = 'agenda-page';
              const ghost = document.createElement('div');
              Object.assign(ghost.style, {
                display: 'grid',
                gridTemplateColumns: colWidths.join(' '),
                width: `${Math.round(totalWidth)}px`,
                borderRadius: '4px',
                overflow: 'hidden',
                opacity: '0.93',
                pointerEvents: 'none',
              });
              visual.forEach(cell => {
                const clone = cell.cloneNode(true);
                clone.style.background = window.getComputedStyle(cell).backgroundColor;
                ghost.appendChild(clone);
              });
              scopePage.appendChild(ghost);
              scopeWrap.appendChild(scopePage);
              document.body.appendChild(scopeWrap);
              e.dataTransfer.setDragImage(ghost, e.clientX - rects[0].left, e.clientY - rects[0].top);
              setTimeout(() => scopeWrap.parentNode?.removeChild(scopeWrap), 0);
            }
            onDragStart?.();
          } : undefined}
          onDragEnd={() => onDragEnd?.()}
        >≡</span>
        <QuoteBadge event={event} />
      </div>

      {/* Col 2 — Evento */}
      <div className="ev-cell ev-cell-evento">
        <div className="ev-evento-header">
          <div className="ev-evento-titles">
            <div className="ev-evento-main">
              <InlineField
                value={event.name || ''}
                onSave={v => saveField('name', v)}
                canEdit={false}
                placeholder="Sin nombre"
              />
            </div>
            {(event.place || canOperate) && (
              <div className="ev-evento-place">
                <LugarPicker
                  value={event.place || ''}
                  lugares={lugares}
                  canEdit={canOperate}
                  onSave={v => saveField('place', v)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="ev-evento-bottom">
          <div className="ev-evento-meta">
            <span className="ev-meta-client">
              <ClientPicker
                value={event.client || ''}
                clients={clients}
                canEdit={canOperate}
                onSave={v => saveField('client', v)}
              />
            </span>
            <span className="ev-meta-sep">·</span>
            <span className="ev-meta-referent">
              <ReferentPicker
                value={event.sales_referent || ''}
                salesRefs={salesRefs}
                canEdit={canOperate}
                onSave={v => saveField('sales_referent', v)}
              />
            </span>
            <span className={`ev-cl-badge ag-cl-${badge.tone}`}>{badge.label}</span>
          </div>
          {event.first_armado_date && (
            <button
              type="button"
              className="ev-armado-link"
              onClick={e => { e.stopPropagation(); onScrollToArmado?.(event.id, event.first_armado_date); }}
              title={`Ir al armado (${String(event.first_armado_date).slice(0, 10)})`}
            >
              IR A ARMADO
            </button>
          )}
        </div>
      </div>

      <AgendaHoursCell
        className="ev-cell ev-cell-hours"
        rows={[
          { key: 'setup', label: 'ARM', value: timeValue('setup_start'), onSave: v => saveField('setup_start', parseTime(v)), canEdit: canOperate, tabGroup, tabIndex: 4 },
          { key: 'start', label: 'INI', value: timeValue('start_time'), onSave: v => saveField('start_time', parseTime(v)), canEdit: canOperate, tabGroup, tabIndex: 5 },
          { key: 'end', label: 'FIN', value: timeValue('end_time'), onSave: v => saveField('end_time', parseTime(v)), canEdit: canOperate, tabGroup, tabIndex: 6 },
        ]}
      />

      {/* Cols 6-8 — Staff (armado / operación / desarmado).
          Se puede asignar gente aunque el evento esté cancelado (locked): usa
          canOperateProp (permiso base) y no canOperate (que suma !locked).
          El resto de los campos siguen bloqueados en cancelados. */}
      <StaffEditor
        eventId={event.id}
        initialAssignments={event.staff_assignments || []}
        initialSections={event.staff_sections || []}
        assignmentModel={assignmentModel}
        assignmentModelError={assignmentModelError}
        retryAssignmentModel={retryAssignmentModel}
        personnel={personnel || []}
        canEdit={canOperateProp}
        tabGroup={tabGroup}
        tabIndexStart={7}
        entityDate={event.event_date?.slice(0, 10)}
        startTime={event.start_time?.slice(0, 5)}
        endTime={event.end_time?.slice(0, 5)}
        externalConflictKeys={conflictKeys}
        onCreateAuxOperation={(type, anchorEl, options) => onCreateAuxOperation?.(event, type, anchorEl, options)}
      />

      {/* Col 9 — Notas */}
      <div
        className="ev-cell ev-cell-notes"
        onClick={canOperate ? () => tabGroup.current[10]?.() : undefined}
        style={{ cursor: canOperate ? 'text' : undefined }}
      >
        <InlineField
          value={event.notes || ''}
          onSave={v => saveField('notes', v)}
          canEdit={canOperate}
          multiline
          tabGroup={tabGroup}
          tabIndex={10}
          placeholder=""
        />
      </div>

      {/* Col 8 — Acciones: el acceso a equipos depende de su permiso específico. */}
      <div className="ev-cell ev-cell-actions">
        {canViewEventEquipment && (
          <button
            type="button"
            className="ev-teams-btn"
            onClick={handleViewTeams}
            disabled={navigating}
            title="Ver equipos"
          >
            Equipos
          </button>
        )}
        <KebabMenu
          ref={kebabRef}
          items={menuItems}
          mobileFirstItem={canViewEventEquipment ? { label: 'Ver equipos', onClick: handleViewTeams } : null}
        />
      </div>

      {archiveOpen && createPortal(
        <div className="modal-overlay" onClick={() => setArchiveOpen(false)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Archivar evento</h3>
            <div className="form-group">
              <label className="form-label">Motivo (opcional)</label>
              <input
                className="input"
                value={archiveReason}
                onChange={e => setArchiveReason(e.target.value)}
                placeholder="Ej: evento cancelado por el cliente"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setArchiveOpen(false)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={handleArchive}>Archivar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {duplicateOpen && createPortal(
        <div className="modal-overlay" onClick={() => setDuplicateOpen(false)}>
          <div className="modal" style={{ maxWidth: 340 }} onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Duplicar evento</h3>
            <div className="form-group">
              <label className="form-label">Fecha del duplicado</label>
              <input
                className="input"
                type="date"
                value={duplicateDate}
                onChange={e => setDuplicateDate(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setDuplicateOpen(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleDuplicate} disabled={!duplicateDate}>Duplicar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
